import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ref, push, onValue } from "firebase/database";
import { db } from "../firebase";
import "./GuestMessages.css";

interface Message {
  id: string;
  name: string;
  message: string;
  createdAt: number | null;
}

const GuestMessages: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const messagesRef = ref(db, "messages");
    const unsubscribe = onValue(
      messagesRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const msgs: Message[] = Object.entries(data)
            .map(([key, value]: [string, any]) => ({
              id: key,
              name: value.name,
              message: value.message,
              createdAt: value.createdAt,
            }))
            .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
          setMessages(msgs);
        } else {
          setMessages([]);
        }
      },
      (error) => {
        console.error("Realtime DB listener error:", error.message);
      },
    );

    return () => unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) return;

    setIsSubmitting(true);
    try {
      const messagesRef = ref(db, "messages");
      await push(messagesRef, {
        name: name.trim(),
        message: message.trim(),
        createdAt: Date.now(),
      });
      setName("");
      setMessage("");
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 3000);
    } catch (error: any) {
      console.error("Error sending message:", error);
      alert("Gagal mengirim pesan: " + (error?.message || "Unknown error"));
    }
    setIsSubmitting(false);
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <section className="guest-messages-section">
      <motion.h2
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="messages-title"
      >
        Doa & Ucapan
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="messages-subtitle"
      >
        Berikan doa dan ucapan terbaik untuk kedua mempelai
      </motion.p>

      {/* Message Form */}
      <motion.form
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="message-form"
        onSubmit={handleSubmit}
      >
        <div className="form-group">
          <input
            type="text"
            placeholder="Nama Anda"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="form-input"
            required
          />
        </div>
        <div className="form-group">
          <textarea
            placeholder="Tulis doa atau ucapan..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="form-textarea"
            rows={4}
            required
          />
        </div>
        <button
          type="submit"
          className="submit-btn"
          disabled={isSubmitting || !name.trim() || !message.trim()}
        >
          {isSubmitting ? "Mengirim..." : "Kirim Ucapan"}
        </button>

        <AnimatePresence>
          {submitted && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="success-msg"
            >
              Terima kasih atas doa dan ucapannya!
            </motion.p>
          )}
        </AnimatePresence>
      </motion.form>

      {/* Messages Display */}
      <div className="messages-list">
        <AnimatePresence>
          {messages.map((msg, index) => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="message-card"
            >
              <div className="message-header">
                <div className="message-avatar">
                  {msg.name.charAt(0).toUpperCase()}
                </div>
                <div className="message-info">
                  <h4 className="message-name">{msg.name}</h4>
                  <span className="message-date">
                    {formatDate(msg.createdAt)}
                  </span>
                </div>
              </div>
              <p className="message-text">{msg.message}</p>
            </motion.div>
          ))}
        </AnimatePresence>

        {messages.length === 0 && (
          <p className="no-messages">
            Jadilah yang pertama mengirim doa dan ucapan!
          </p>
        )}
      </div>
    </section>
  );
};

export default GuestMessages;
